import { z } from 'zod'

/**
 * TomTom Fuzzy Search v2 response → PWA Result row mapper (Plan 02-03 Task 3).
 *
 * Why a loose Zod parse on the envelope:
 *   - The Worker already enforces auth + upstream shape; the PWA only needs
 *     to defensively skip malformed entries rather than reject the whole
 *     response.
 *   - TomTom's Fuzzy response has many optional fields; we only care about
 *     `position.lat`/`position.lon` + one of `poi.name` / `address.freeformAddress`.
 *
 * Skip rules (per plan <behavior> contract):
 *   - Missing position.lat or position.lon → drop the result
 *   - Missing BOTH poi.name and address.freeformAddress → drop the result
 *   - Missing id → synthesise from rounded coordinates
 *   - Zod parse failure on the envelope (e.g. `results: 'nope'`) → return []
 */

const FuzzyEntrySchema = z
  .object({
    id: z.string().optional(),
    type: z.string().optional(),
    poi: z.object({ name: z.string().optional() }).optional(),
    address: z.object({ freeformAddress: z.string().optional() }).optional(),
    position: z
      .object({
        lat: z.number(),
        lon: z.number(),
      })
      .optional(),
  })
  .passthrough()

const FuzzySearchResponseSchema = z
  .object({
    results: z.array(FuzzyEntrySchema),
  })
  .passthrough()

export type Result = {
  id: string
  label: string
  sublabel?: string
  lat: number
  lng: number
}

export function mapTomTomResults(raw: unknown): Result[] {
  const parsed = FuzzySearchResponseSchema.safeParse(raw)
  if (!parsed.success) return []

  const out: Result[] = []
  for (const entry of parsed.data.results) {
    const pos = entry.position
    if (!pos) continue

    const poiName = entry.poi?.name
    const addr = entry.address?.freeformAddress
    if (!poiName && !addr) continue

    const label = poiName ?? addr!
    const sublabel =
      poiName && addr && poiName !== addr ? addr : undefined

    const id = entry.id ?? `${pos.lat.toFixed(5)}:${pos.lon.toFixed(5)}`

    out.push({
      id,
      label,
      sublabel,
      lat: pos.lat,
      lng: pos.lon,
    })
  }
  return out
}
